// src/App.js
import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router';
import { StytchProvider } from '@stytch/react';
import { library } from '@fortawesome/fontawesome-svg-core';

// Import solid icons for your Layout menu
import {
  faTachometerAlt,
  faRocket,
  faHistory,
  faChartLine,
  faUsers,
  faFlask,
  faCodeBranch,
  faBars,
  faSignOutAlt,
  faDollarSign,
  faSearch,
  faChartArea,
  faCog,
  faFileCode
} from '@fortawesome/free-solid-svg-icons';

// Import brand icons
import {
  faGithub,
  faGoogle
} from '@fortawesome/free-brands-svg-icons';
import { AppAuthProvider } from './auth/AppAuthContext';
import ProxyLoginPage from './components/ProxyLoginPage';
import StytchLogin from './components/StytchLogin';
import StytchLogoutPage from './components/StytchLogoutPage';
import OAuthAuthorizePage from './components/OAuthAuthorizePage';
import SigningInScreen from './components/SigningInScreen';
import AcquisitionTracker from './components/AcquisitionTracker';
import LicenseBanner from './components/LicenseBanner';
import { cookieOptions, stytchClient } from './utils/stytch';
import { isE2ETestMode, isE2EAuthenticatedRoutesEnabled, isOnPremMode, isSaaSMode, isStytchEnabled } from './utils/runtimeConfig';
import { buildLoginOriginDashboardRedirectTarget } from './utils/redirects';
import './App.css';

// The login origin serves the same image as the dashboard, but must not load
// its charts, editors, and other application routes before authentication.
const DashboardApp = lazy(() => import('./components/DashboardApp'));
const DashboardErrorProvider = lazy(() => import('./components/ErrorContext').then(module => ({ default: module.ErrorProvider })));

let E2E_HARNESS_ROUTES = [];
if (process.env.NODE_ENV !== 'production' && isE2ETestMode) {
  const E2EChartPreview = require('./components/E2EChartPreview').default;
  const E2EProjectSwitchingHarness = require('./components/E2EProjectSwitchingHarness').default;
  const E2EBazelSummaryHarness = require('./components/E2EBazelSummaryHarness').default;
  const E2EPerfettoHarness = require('./components/E2EPerfettoHarness').default;
  const E2EBBConfigHarness = require('./components/E2EBBConfigHarness').default;
  const E2EDiffViewerHarness = require('./components/E2EDiffViewerHarness').default;
  const E2EInvocationOverviewHarness = require('./components/E2EInvocationOverviewHarness').default;

  E2E_HARNESS_ROUTES = [
    { path: '/e2e-chart-preview', Component: E2EChartPreview },
    { path: '/e2e-project-switching/*', Component: E2EProjectSwitchingHarness },
    { path: '/e2e-bazel-summary', Component: E2EBazelSummaryHarness },
    { path: '/e2e-perfetto', Component: E2EPerfettoHarness },
    { path: '/e2e-bb-config', Component: E2EBBConfigHarness },
    // BBConfigEditor now drives its state from the URL and navigate()s to
    // absolute /bb-config[...] paths (see AuthenticatedApp.js's own Route
    // registrations for the real, authenticated versions of these). Without
    // these two harness entries, clicking anything that navigates away from
    // /e2e-bb-config falls through to the "*" catch-all below and renders the
    // real, authenticated AuthenticatedApp instead of staying on the
    // unauthenticated E2E harness -- which is why ConfigBrowser never mounts.
    ...(!isE2EAuthenticatedRoutesEnabled ? [
      { path: '/bb-config', Component: E2EBBConfigHarness },
      { path: '/bb-config/:configSetName', Component: E2EBBConfigHarness },
    ] : []),
    { path: '/e2e-diff-viewer', Component: E2EDiffViewerHarness },
    { path: '/e2e-invocation-overview', Component: E2EInvocationOverviewHarness },
  ];
}

// Add all icons to the library
library.add(
  // Solid icons for your menu and UI
  faTachometerAlt,
  faRocket,
  faHistory,
  faChartLine,
  faUsers,
  faFlask,
  faCodeBranch,
  faBars,
  faSignOutAlt,
  faDollarSign,
  faSearch,
  faChartArea,
  faCog,
  faFileCode,

  // Brand icons
  faGithub,
  faGoogle
);

function LoginOriginDashboardRedirect({ target }) {
  useEffect(() => {
    window.location.replace(target);
  }, [target]);
  return null;
}

function DashboardContent({ children }) {
  return (
    <Suspense fallback={<SigningInScreen title="Loading your dashboard…" />}>
      <DashboardErrorProvider>{children}</DashboardErrorProvider>
    </Suspense>
  );
}

function AppRoutes() {
  return (
    <Router>
      {isSaaSMode && <AcquisitionTracker />}
      {isOnPremMode && <LicenseBanner />}
      <Routes>
        <Route path="/login" element={isStytchEnabled ? <StytchLogin /> : <ProxyLoginPage />} />
        <Route path="/authorize" element={isStytchEnabled ? <OAuthAuthorizePage /> : <Navigate to="/" replace />} />
        {/* Login-origin logout: revokes the Stytch SSO session that oauth2-proxy's
            /oauth2/sign_out cannot reach. On the proxy-mode dashboard there is no
            Stytch client, so /logout is a no-op that returns home. */}
        <Route path="/logout" element={isStytchEnabled ? <StytchLogoutPage /> : <Navigate to="/" replace />} />
        {E2E_HARNESS_ROUTES.map(({ path, Component }) => (
          <Route key={path} path={path} element={<DashboardContent><Component /></DashboardContent>} />
        ))}
        <Route path="*" element={
          <Suspense fallback={<SigningInScreen title="Loading your dashboard…" />}>
            <DashboardApp />
          </Suspense>
        } />
      </Routes>
    </Router>
  );
}

function App() {
  // The Stytch app serves login and OIDC consent only. Send all dashboard
  // routes to the oauth2-proxy protected host before loading dashboard state.
  if (isStytchEnabled && !['/login', '/authorize', '/logout'].includes(window.location.pathname)) {
    const path = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const target = buildLoginOriginDashboardRedirectTarget(path);
    if (target) return <LoginOriginDashboardRedirect target={target} />;
  }

  const appContent = (
    <AppAuthProvider>
      <AppRoutes />
    </AppAuthProvider>
  );

  return (
    <>
      {isStytchEnabled ? (
        <StytchProvider stytch={stytchClient} cookieOptions={cookieOptions}>
          {appContent}
        </StytchProvider>
      ) : (
        appContent
      )}
    </>
  );
}

export default App;
